<template>
  <div class="video-player">
    <div class="player-position">
      <div id="player"></div>
      <div
        v-show="nextContentText && isAdvanceContentButtonShown"
        class="video-player__advance"
        @click="advanceToNextVideo()"
      >
        <button class="video-player__advance-btn">
          {{ nextContentText }} >>
        </button>
      </div>
    </div>
    <transition name="fade" mode="out-in">
      <CreateBookmarkModal
        v-if="showCreateBookmarkModal"
        @resumeVideo="resumeVideo"
        @closed="showCreateBookmarkModal = false"
      ></CreateBookmarkModal>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  sendSQSOldHeartbeat,
  sendSQSNewHeartbeat,
  sendPinpointHeartbeat,
} from '@/services/heartbeat'
import CreateBookmarkModal from '@/components/bookmark/CreateBookmarkModal.vue'

export default {
  name: 'VideoPlayer',
  components: {
    CreateBookmarkModal,
  },
  data() {
    return {
      instance: [],
      delta: 0,
      config: null,
      playerContainerEl: null,
      playerHeight: null,
      totalDuration: 0,
      showCreateBookmarkModal: false,
      lastTime: 0,
      currentTime: 0,
      markedWatched: false,
      markedActive: false,
      isAdvanceContentButtonShown: false,
    }
  },
  props: {
    video: {
      type: Object,
      required: true,
    },
    nextContent: {
      type: String,
      required: false,
    },
  },
  computed: {
    ...mapGetters({
      jwVideo: 'content/jwVideo',
      course: 'courses/courseWithTopicLessons',
      activeCourses: 'user/userActiveCourses',
      progress: 'user/progress',
      profile: 'auth/userProfile',
      userId: 'auth/userId',
      videoTime: 'content/playingVideoTime',
      isFullscreen: 'content/isWatchingFullscreen',
      isCordova: 'app/isCordova',
    }),
    activeCourse() {
      return !!this.activeCourses.find(
        activeCourse => activeCourse.id === this.course.id
      )
    },
    hasWatched() {
      if (this.progress || this.progress.length > 0) {
        return !!this.progress.videos.find(
          watched => watched.id === this.jwVideo.id
        )
      }
      return false
    },
    nextContentText() {
      switch (this.nextContent) {
        case 'quiz':
          return 'Take Quiz'
        case 'video':
          return 'Play next video'
        default:
          return ''
      }
    },
    isProduction() {
      return process.env.VUE_APP_ENVIRONMENT === 'production'
    },
  },
  methods: {
    setupPlayer(playlist) {
      this.instance = this.$jwplayer('player')
        .setup({
          key: process.env.VUE_APP_JWPLAYER_KEY,
          displaytitle: true,
          logo: true,
          playlist,
          width: '100%',
          cast: {},
          floating: {},
          skin: { name: 'ine' },
          aspectratio: '16:9',
          visualplaylist: false,
          controls: true,
          autostart: 'viewable',
          playbackRateControls: [0.75, 1, 1.25, 1.5, 2, 2.5, 3],
        })
        .on('ready', e => {
          this.config = this.instance.getConfig()
          this.playerHeight = this.config.containerHeight
          this.playerContainerEl.style.height = `${this.playerHeight}px`
          this.isScrollTimeout = false
          const player = document.querySelector('#player')
          if (player) player.focus()
          this.setupAdvanceContainer()
          this.sendHeartbeat(e)
        })
        .on('play', e => {
          this.totalDuration = this.instance.getDuration()
          if (this.videoTime !== 0) {
            this.instance.seek(this.videoTime)
            this.$store.dispatch('content/setPlayingVideoTime', 0)
          }
          this.sendHeartbeat(e)
        })
        .on('pause', e => {
          this.sendHeartbeat(e)
        })
        .on('complete', e => {
          this.sendHeartbeat(e)
          this.markVideoAsWatched()
          this.advanceToNextVideo()
        })
        .on('error', e => {
          this.sendHeartbeat(e)
        })
        .on('fullscreen', () => {
          if (!this.instance.getFullscreen()) {
            setTimeout(() => {
              this.$scrollTo('.video-player', 10, {
                container: 'body',
                offset: -220,
              })
            }, 100)
          }
        })
        .on('time', e => {
          this.currentTime = parseInt(this.instance.getPosition())
          if (this.currentTime % 5 || this.currentTime === 0) {
            if (this.lastTime !== this.currentTime) {
              this.delta++
            }
            this.lastTime = this.currentTime // Fix for JWPlayer polling multiple times
          } else {
            if (this.lastTime !== this.currentTime) {
              this.delta++
              this.sendHeartbeat(e)
              this.delta = 0
            }
            this.lastTime = this.currentTime // Fix for JWPlayer polling multiple times
          }
          if (this.currentTime && this.currentTime === 15) {
            this.markCourseAsActive()
          }
          if (this.currentTime === parseInt(this.totalDuration) - 30) {
            this.markVideoAsWatched()
          }
          if (this.currentTime >= parseInt(this.totalDuration) - 15) {
            this.isAdvanceContentButtonShown = true
          } else {
            this.isAdvanceContentButtonShown = false
          }
        })
      this._addBookmarksButton(this.instance)
    },
    resumeVideo() {
      this.instance.play()
    },
    _addBookmarksButton() {
      this.instance.addButton(
        // This portion is what designates the graphic used for the button
        '/assets/icons/jwplayer/bookmark_icon.png',
        // This portion determines the text that appears as a tooltip
        'Bookmark Video',
        // This portion designates the functionality of the button itself
        () => {
          this._bookmarkVideo()
          if (this.instance.getFullscreen() === true) {
            this.instance.setFullscreen()
          }
          this.instance.pause()
          // With the below code, we're grabbing the file that's currently playing
          this.showCreateBookmarkModal = true
        },
        // And finally, here we set the unique ID of the button itself.
        'bookmark'
      )
    },
    _bookmarkVideo() {
      const bookmark = {
        course_id: this.course.id,
        title: this.instance.getPlaylistItem().title,
        time: parseInt(this.instance.getPosition()),
        user_id: this.userId,
        video_id: this.instance.getPlaylistItem().vidId,
      }
      this.$store.dispatch('user/storePendingUserBookmark', bookmark)
    },
    async markVideoAsWatched() {
      if (!this.hasWatched && !this.markedWatched) {
        this.markedWatched = true
        const completedVideo = { userId: this.userId, vidId: this.jwVideo.id }
        await this.$store.dispatch('user/markVideoAsWatched', completedVideo)
        const courseIdAndUserId = {
          courseId: this.course.id,
          userId: this.userId,
        }
        await this.$store.dispatch(
          'user/getUserCourseProgress',
          courseIdAndUserId
        )
      }
    },
    async markCourseAsActive() {
      if (!this.activeCourse && !this.markedActive) {
        this.markedActive = true
        const courseAndUserId = {
          courseId: this.course.id,
          userId: this.userId,
        }
        await this.$store.dispatch(
          'user/postToUserActiveCourse',
          courseAndUserId
        )
        await this.$store.dispatch('user/getUserActiveCourses', this.userId)
      }
    },
    advanceToNextVideo() {
      this.$emit('advanceToNextVideo')
    },
    setupAdvanceContainer() {
      const advanceContainer = document.querySelector('.video-player__advance')
      const jwControls = document.querySelector('.jw-controlbar')
      if (jwControls) {
        jwControls.append(advanceContainer)
      }
    },
    sendHeartbeat() {
      if (this.isProduction) {
        sendSQSOldHeartbeat(
          this.delta,
          this.userId,
          this.instance.getPlaylistItem().vidId
        )
        sendSQSNewHeartbeat(
          parseInt(this.instance.getPosition()),
          this.delta,
          this.userId,
          this.instance.getPlaylistItem().vidId,
          this.course.id,
          this.$route.params.slug
        )
      }
      sendPinpointHeartbeat(
        parseInt(this.instance.getPosition()),
        this.delta,
        this.profile.uaaId,
        this.profile.account_id,
        this.profile.team_ids,
        this.instance.getPlaylistItem().vidId,
        this.course.id,
        this.$route.params.slug,
        this.course.instructor_id
      )
    },
  },
  async created() {
    this.$emit('isLoadingVideo', true)
    await this.$store.dispatch('content/getJWVideo', this.video.id)
    const { video } = this.jwVideo
    if (video) this.setupPlayer(video)
    if (this.isCordova) window.plugins.insomnia.keepAwake()
    this.$emit('isLoadingVideo', false)
  },
  mounted() {
    this.playerContainerEl = document.querySelector('.video-player')
    this.$scrollTo('.video-player', 1000, {
      container: 'body',
      offset: -220,
    })
  },
  destroyed() {
    if (this.instance) this.instance.remove()
    if (this.isCordova) window.plugins.insomnia.allowSleepAgain()
  },
}
</script>

<style lang="scss">
.video-player {
  $this: &;
  position: relative;
  &__advance {
    position: absolute;
    bottom: 100%;
    right: 0;
    background: linear-gradient(
      to left bottom,
      rgb(82, 172, 94),
      rgb(135, 189, 140)
    );
    opacity: 0.8;
    width: 200px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    border-top-left-radius: 25px;
    cursor: pointer;
    &:hover {
      opacity: 1;
      #{$this}__advance-btn {
        opacity: 1;
      }
    }
  }
  &__advance-btn {
    background: none;
    border: none;
    color: var(--white);
    font-size: 1rem;
    text-transform: uppercase;
    padding: 0 10% 0 0;
    opacity: 0.8;
    cursor: pointer;
  }
}
</style>
